<template>
  <div class="macd-explorer-tab p-4">
    <!-- Data Selection Controls -->
    <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-4 sm:items-end min-w-0">
      <!-- Symbol Selector -->
      <div class="form-control w-full sm:max-w-xs">
        <label class="label">
          <span class="label-text">Symbol</span>
        </label>
        <div class="relative w-full">
          <input
            v-model="symbolInput"
            type="text"
            placeholder="Type or select symbol"
            class="input input-bordered w-full"
            @focus="showDropdown = true"
            @input="filterSymbols"
            @keydown.enter="selectSymbol(symbolInput)"
            @click.stop
          >
          <ul
            v-if="showDropdown && filteredSymbols.length > 0"
            class="absolute left-0 top-full mt-1 z-50 menu p-2 shadow-lg bg-base-100 rounded-box w-full max-h-60 overflow-y-auto border border-base-300"
            @click.stop
          >
            <li
              v-for="sym in filteredSymbols"
              :key="sym"
            >
              <a @click.stop="selectSymbol(sym)">{{ sym }}</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Timeframe Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Timeframe</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="tf in timeframeOptions"
            :key="tf.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': timeframe === tf.value }"
            @click="timeframe = tf.value"
          >
            {{ tf.label }}
          </button>
        </div>
      </div>

      <!-- Days Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Days</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="d in availableDays"
            :key="d"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': days === d }"
            @click="days = d"
          >
            {{ d }}
          </button>
        </div>
      </div>

      <!-- Area Threshold -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Area Threshold</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="t in thresholdOptions"
            :key="t"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': areaThreshold === t }"
            @click="areaThreshold = t"
          >
            {{ t }}
          </button>
        </div>
      </div>

      <!-- Profit Target -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Profit Target %</span>
        </label>
        <input
          v-model.number="profitTarget"
          type="number"
          step="0.05"
          min="0.01"
          max="10"
          class="input input-sm input-bordered w-24"
        >
      </div>

      <!-- Stop Loss -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Stop Loss %</span>
        </label>
        <input
          v-model.number="stopLoss"
          type="number"
          step="0.05"
          min="0.01"
          max="10"
          class="input input-sm input-bordered w-24"
        >
      </div>

      <!-- Min Wave Bars -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Min Wave Bars</span>
        </label>
        <input
          v-model.number="minWaveBars"
          type="number"
          step="1"
          min="1"
          max="20"
          class="input input-sm input-bordered w-24"
        >
      </div>

      <!-- End Date/Time Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">End Date/Time</span>
        </label>
        <div class="flex items-center gap-1">
          <button
            class="btn btn-xs sm:btn-sm"
            :title="`Go back 1 ${timeframe}`"
            @click="adjustEndDateTime(-1)"
          >
            &lt;
          </button>
          <input
            v-model="endDateTime"
            type="datetime-local"
            class="input input-sm input-bordered w-52"
          >
          <button
            class="btn btn-xs sm:btn-sm"
            :title="`Go forward 1 ${timeframe}`"
            @click="adjustEndDateTime(1)"
          >
            &gt;
          </button>
          <button
            class="btn btn-sm btn-ghost"
            title="Reset to now"
            @click="resetEndDateTime"
          >
            Now
          </button>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">&nbsp;</span>
        </label>
        <button
          class="btn btn-primary w-full sm:w-auto"
          :disabled="isLoading || !symbol"
          @click="fetchMACDData"
        >
          <span
            v-if="isLoading"
            class="loading loading-spinner loading-sm"
          />
          {{ isLoading ? 'Loading...' : 'Load Data' }}
        </button>
      </div>

      <!-- Auto-Refresh Toggle -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">&nbsp;</span>
        </label>
        <button
          class="btn"
          :class="autoRefreshEnabled ? 'btn-warning' : 'btn-outline'"
          :disabled="!hasData"
          @click="autoRefreshEnabled ? stopAutoRefresh() : startAutoRefresh()"
        >
          <span v-if="autoRefreshEnabled">
            Live {{ autoRefreshCountdown }}s
          </span>
          <span v-else>
            Auto-Refresh
          </span>
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="alert alert-error mb-4"
    >
      <span>{{ error }}</span>
    </div>

    <!-- Sub-tabs: Analysis | Backtest -->
    <div
      v-if="hasData"
      class="tabs tabs-boxed mb-4"
    >
      <a
        class="tab"
        :class="{ 'tab-active': activeTab === 'analysis' }"
        @click="activeTab = 'analysis'"
      >
        Analysis
      </a>
      <a
        class="tab"
        :class="{ 'tab-active': activeTab === 'backtest' }"
        @click="activeTab = 'backtest'"
      >
        Backtest
      </a>
    </div>

    <!-- Current Signal Card -->
    <div
      v-if="hasData && currentWave"
      class="card shadow-xl mb-4 border"
      :class="signalCardClass"
    >
      <!-- Status Bar -->
      <div
        class="h-2 rounded-t-xl"
        :class="signalBarClass"
      />

      <div class="card-body p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <h2 class="text-2xl font-bold">
                {{ currentWave.signal || 'NO SIGNAL' }}
              </h2>
              <span
                v-if="currentWave.signal_confidence"
                class="badge badge-lg"
                :class="confidenceBadgeClass"
              >
                {{ currentWave.signal_confidence }}
              </span>
            </div>
            <p class="text-base opacity-80">
              {{ currentWave.signal_reason || 'Waiting for wave completion' }}
            </p>
          </div>

          <!-- Area Ratio Display -->
          <div class="text-right">
            <div class="text-sm opacity-60 mb-1">
              Area Ratio
            </div>
            <div
              class="text-3xl font-bold font-mono"
              :class="areaRatioColor"
            >
              {{ currentWave.area_ratio ? currentWave.area_ratio.toFixed(2) : 'N/A' }}
            </div>
            <div class="text-xs opacity-60">
              Threshold: {{ areaThreshold }}
            </div>
          </div>
        </div>

        <!-- Quick Stats Row -->
        <div class="flex flex-wrap gap-3 mt-4">
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Wave Direction
            </div>
            <div
              class="stat-value text-lg"
              :class="currentWave.wave_direction === 'bullish' ? 'text-success' : 'text-error'"
            >
              {{ capitalizeFirst(currentWave.wave_direction) }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Wave Area
            </div>
            <div class="stat-value text-lg font-mono">
              {{ currentWave.wave_area?.toFixed(2) || 'N/A' }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Wave Bars
            </div>
            <div class="stat-value text-lg font-mono">
              {{ currentWave.wave_bars || 'N/A' }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Status
            </div>
            <div class="stat-value text-lg">
              {{ currentWave.is_completed ? 'Completed' : 'In Progress' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Forming Signal Card (Early Detection) -->
    <div
      v-if="hasData && formingData?.has_forming_candle"
      class="card shadow-xl mb-4 border-2 border-dashed"
      :class="formingSignalCardClass"
    >
      <!-- Status Bar (animated) -->
      <div
        class="h-2 rounded-t-xl animate-pulse"
        :class="formingSignalBarClass"
      />

      <div class="card-body p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <span class="badge badge-outline badge-sm">FORMING</span>
              <h2 class="text-2xl font-bold">
                {{ formingData.projected_signal || 'WATCHING' }}
              </h2>
              <span
                v-if="formingData.projected_confidence"
                class="badge badge-lg"
                :class="formingConfidenceBadgeClass"
              >
                {{ formingData.projected_confidence }}
              </span>
            </div>
            <p class="text-base opacity-80">
              <template v-if="formingData.would_cross_zero && formingData.projected_signal">
                Zero crossing detected — {{ formingData.projected_signal }} signal would fire
              </template>
              <template v-else-if="formingData.would_cross_zero">
                Zero crossing detected but no signal (ratio above threshold)
              </template>
              <template v-else>
                No zero crossing yet — histogram same sign
              </template>
            </p>
          </div>

          <!-- Projected Ratio -->
          <div class="text-right">
            <div class="text-sm opacity-60 mb-1">
              Projected Ratio
            </div>
            <div
              class="text-3xl font-bold font-mono"
              :class="formingRatioColor"
            >
              {{ formingData.projected_ratio !== null && formingData.projected_ratio !== undefined ? formingData.projected_ratio.toFixed(4) : 'N/A' }}
            </div>
            <div class="text-xs opacity-60">
              {{ formingData.minutes_remaining?.toFixed(0) }} min remaining
            </div>
          </div>
        </div>

        <!-- Quick Stats Row -->
        <div class="flex flex-wrap gap-3 mt-4">
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Confirmed Histogram
            </div>
            <div
              class="stat-value text-lg font-mono"
              :class="formingData.confirmed_histogram >= 0 ? 'text-success' : 'text-error'"
            >
              {{ formingData.confirmed_histogram?.toFixed(4) }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Forming Histogram
            </div>
            <div
              class="stat-value text-lg font-mono"
              :class="formingData.forming_histogram >= 0 ? 'text-success' : 'text-error'"
            >
              {{ formingData.forming_histogram?.toFixed(4) }}
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Zero Cross
            </div>
            <div class="stat-value text-lg">
              {{ formingData.would_cross_zero ? 'YES' : 'NO' }}
            </div>
          </div>
          <div
            v-if="formingData.projected_wave"
            class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]"
          >
            <div class="stat-title text-xs">
              Completing Wave
            </div>
            <div
              class="stat-value text-lg"
              :class="formingData.projected_wave.direction === 'bullish' ? 'text-success' : 'text-error'"
            >
              {{ capitalizeFirst(formingData.projected_wave.direction) }}
              ({{ formingData.projected_wave.bars }} bars)
            </div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-3 flex-1 min-w-[120px]">
            <div class="stat-title text-xs">
              Current Price
            </div>
            <div class="stat-value text-lg font-mono">
              ${{ formatPrice(formingData.current_price) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Area Ratio Stats Panel -->
    <div
      v-if="hasData && areaRatioStats.count > 0"
      class="card bg-base-200 shadow mb-4"
    >
      <div class="card-body p-4">
        <h4 class="font-semibold mb-3">
          Area Ratio Distribution
          <span class="badge badge-ghost ml-2">{{ areaRatioStats.count }} waves</span>
        </h4>
        <div class="flex flex-wrap gap-4">
          <div class="stat bg-base-300 rounded-lg p-3 flex-1 min-w-[100px]">
            <div class="stat-title text-xs">
              Min
            </div>
            <div class="stat-value text-lg font-mono text-error">
              {{ areaRatioStats.min?.toFixed(2) }}
            </div>
          </div>
          <div class="stat bg-base-300 rounded-lg p-3 flex-1 min-w-[100px]">
            <div class="stat-title text-xs">
              Max
            </div>
            <div class="stat-value text-lg font-mono text-success">
              {{ areaRatioStats.max?.toFixed(2) }}
            </div>
          </div>
          <div class="stat bg-base-300 rounded-lg p-3 flex-1 min-w-[100px]">
            <div class="stat-title text-xs">
              Average
            </div>
            <div class="stat-value text-lg font-mono">
              {{ areaRatioStats.avg?.toFixed(2) }}
            </div>
          </div>
          <div class="stat bg-base-300 rounded-lg p-3 flex-1 min-w-[100px]">
            <div class="stat-title text-xs">
              Median
            </div>
            <div class="stat-value text-lg font-mono">
              {{ areaRatioStats.median?.toFixed(2) }}
            </div>
          </div>
          <div class="stat bg-base-300 rounded-lg p-3 flex-1 min-w-[100px]">
            <div class="stat-title text-xs">
              Below {{ areaThreshold }}
            </div>
            <div
              class="stat-value text-lg font-mono"
              :class="areaRatioStats.belowThreshold > 0 ? 'text-warning' : 'text-base-content/50'"
            >
              {{ areaRatioStats.belowThreshold }}
            </div>
            <div class="stat-desc">
              {{ areaRatioStats.signalPct }}% of waves
            </div>
          </div>
        </div>
        <div
          v-if="areaRatioStats.belowThreshold === 0"
          class="alert alert-info mt-3"
        >
          <span>No signals at current threshold ({{ areaThreshold }}). Try increasing the threshold to {{ Math.min(areaRatioStats.median?.toFixed(2) || 0.85, 0.95) }} based on median.</span>
        </div>
      </div>
    </div>

    <!-- Analysis Tab Content -->
    <div
      v-if="hasData && activeTab === 'analysis'"
      class="space-y-4"
    >
      <!-- Price Chart with Wave Overlays -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold text-lg">
            Price with MACD Wave Overlays
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-success badge-sm">Green = Bullish Wave</span>
            <span class="badge badge-error badge-sm">Red = Bearish Wave</span>
          </div>
        </div>
        <div class="flex justify-between items-center mb-2">
          <p class="text-xs text-base-content/60">
            Shaded regions show MACD histogram waves. Signal markers appear at wave completion when area ratio &lt; {{ areaThreshold }}.
          </p>
          <p
            v-if="formattedDataSource"
            class="text-xs text-base-content/50"
          >
            Data: {{ formattedDataSource.exchange }} | Last: {{ formattedDataSource.lastCandle }}
          </p>
        </div>
        <div
          ref="priceChart"
          class="chart-panel-large"
        />
      </div>

      <!-- MACD Histogram Chart -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            MACD Histogram
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-info badge-sm">Blue = MACD Line</span>
            <span class="badge badge-warning badge-sm">Orange = Signal Line</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Histogram bars colored by wave direction. Wave areas are the cumulative sum between zero-crossings.
        </p>
        <div
          ref="macdChart"
          class="chart-panel"
        />
      </div>

      <!-- Area Ratio Chart -->
      <div class="bg-base-200 rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-semibold">
            Area Ratio Timeline
          </h4>
          <div class="flex gap-2 text-xs">
            <span class="badge badge-error badge-sm">&lt; {{ areaThreshold }} = Exhaustion Signal</span>
          </div>
        </div>
        <p class="text-xs text-base-content/60 mb-2">
          Ratio of current wave area to previous same-direction wave. Values below {{ areaThreshold }} indicate momentum exhaustion.
        </p>
        <div
          ref="areaRatioChart"
          class="chart-panel"
        />
      </div>

      <!-- Wave Table -->
      <div class="bg-base-200 rounded-lg p-4">
        <h4 class="font-semibold mb-3">
          Recent Waves
        </h4>
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>Direction</th>
                <th>Start</th>
                <th>End</th>
                <th>Bars</th>
                <th>Area</th>
                <th>Ratio</th>
                <th>Signal</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="wave in recentWaves"
                :key="wave.wave_id"
                :class="wave.signal ? 'bg-base-300' : ''"
              >
                <td>{{ wave.wave_id }}</td>
                <td :class="wave.direction === 'bullish' ? 'text-success' : 'text-error'">
                  {{ capitalizeFirst(wave.direction) }}
                </td>
                <td class="text-xs">
                  {{ formatDate(wave.start_date) }}
                </td>
                <td class="text-xs">
                  {{ formatDate(wave.end_date) }}
                </td>
                <td>{{ wave.bar_count }}</td>
                <td class="font-mono">
                  {{ wave.area?.toFixed(2) }}
                </td>
                <td
                  class="font-mono"
                  :class="wave.area_ratio && wave.area_ratio < areaThreshold ? 'text-error font-bold' : ''"
                >
                  {{ wave.area_ratio?.toFixed(3) || '-' }}
                </td>
                <td>
                  <span
                    v-if="wave.signal"
                    class="badge badge-sm"
                    :class="wave.signal === 'LONG' ? 'badge-success' : 'badge-error'"
                  >
                    {{ wave.signal }}
                  </span>
                </td>
                <td>
                  <span
                    v-if="wave.signal_confidence"
                    class="badge badge-sm"
                    :class="wave.signal_confidence === 'HIGH' ? 'badge-success' : wave.signal_confidence === 'MEDIUM' ? 'badge-warning' : 'badge-ghost'"
                  >
                    {{ wave.signal_confidence }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Backtest Tab Content -->
    <div
      v-if="hasData && activeTab === 'backtest'"
      class="space-y-4"
    >
      <!-- Performance Stats -->
      <div class="stats stats-vertical lg:stats-horizontal shadow w-full bg-base-200">
        <div class="stat">
          <div class="stat-title">
            Total Return
          </div>
          <div
            class="stat-value text-lg"
            :class="backtest.total_return >= 0 ? 'text-success' : 'text-error'"
          >
            {{ backtest.total_return >= 0 ? '+' : '' }}{{ backtest.total_return?.toFixed(2) }}%
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Win Rate
          </div>
          <div class="stat-value text-lg text-primary">
            {{ backtest.win_rate?.toFixed(1) }}%
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Profit Factor
          </div>
          <div class="stat-value text-lg">
            {{ backtest.profit_factor?.toFixed(2) }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Max Drawdown
          </div>
          <div class="stat-value text-lg text-error">
            {{ backtest.max_drawdown?.toFixed(2) }}%
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Total Trades
          </div>
          <div class="stat-value text-lg">
            {{ backtest.total_trades }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Avg Hold Time
          </div>
          <div class="stat-value text-lg">
            {{ backtest.avg_hold_time?.toFixed(1) }} bars
          </div>
        </div>
      </div>

      <!-- Trade Breakdown -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <!-- Long/Short Breakdown -->
        <div class="card bg-base-200">
          <div class="card-body p-4">
            <h4 class="font-semibold mb-3">
              Direction Breakdown
            </h4>
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <span class="text-success">Long Trades</span>
                <span class="font-mono">{{ backtest.long_trades }} ({{ backtest.long_win_rate?.toFixed(1) }}% win)</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-error">Short Trades</span>
                <span class="font-mono">{{ backtest.short_trades }} ({{ backtest.short_win_rate?.toFixed(1) }}% win)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Win/Loss Stats -->
        <div class="card bg-base-200">
          <div class="card-body p-4">
            <h4 class="font-semibold mb-3">
              Win/Loss Statistics
            </h4>
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <span>Average Win</span>
                <span class="font-mono text-success">+{{ backtest.avg_win?.toFixed(3) }}%</span>
              </div>
              <div class="flex justify-between items-center">
                <span>Average Loss</span>
                <span class="font-mono text-error">{{ backtest.avg_loss?.toFixed(3) }}%</span>
              </div>
              <div class="flex justify-between items-center">
                <span>Best Trade</span>
                <span class="font-mono text-success">+{{ backtest.best_trade?.toFixed(3) }}%</span>
              </div>
              <div class="flex justify-between items-center">
                <span>Worst Trade</span>
                <span class="font-mono text-error">{{ backtest.worst_trade?.toFixed(3) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Exit Reason Distribution -->
      <div class="card bg-base-200">
        <div class="card-body p-4">
          <h4 class="font-semibold mb-3">
            Exit Reason Distribution
          </h4>
          <div class="flex flex-wrap gap-4">
            <div
              v-for="(count, reason) in backtest.exit_distribution"
              :key="reason"
              class="stat bg-base-300 rounded-lg p-3"
            >
              <div class="stat-title text-xs">
                {{ formatExitReason(reason) }}
              </div>
              <div class="stat-value text-lg">
                {{ count }}
              </div>
              <div class="stat-desc">
                {{ ((count / backtest.total_trades) * 100).toFixed(0) }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Equity Curve -->
      <div class="bg-base-200 rounded-lg p-4">
        <h4 class="font-semibold mb-2">
          Equity Curve
        </h4>
        <div
          ref="equityChart"
          class="chart-panel"
        />
      </div>

      <!-- Trade Table -->
      <div class="bg-base-200 rounded-lg p-4">
        <h4 class="font-semibold mb-3">
          Trade History
        </h4>
        <div class="overflow-x-auto max-h-96">
          <table class="table table-sm table-pin-rows">
            <thead>
              <tr>
                <th>#</th>
                <th>Direction</th>
                <th>Entry Date</th>
                <th>Entry Price</th>
                <th>Exit Date</th>
                <th>Exit Price</th>
                <th>Exit Reason</th>
                <th>P&L</th>
                <th>Hold Time</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(trade, idx) in backtest.trades"
                :key="idx"
                :class="trade.pnl_pct >= 0 ? 'bg-success/10' : 'bg-error/10'"
              >
                <td>{{ idx + 1 }}</td>
                <td :class="trade.direction === 'LONG' ? 'text-success' : 'text-error'">
                  {{ trade.direction }}
                </td>
                <td class="text-xs">
                  {{ formatDate(trade.entry_date) }}
                </td>
                <td class="font-mono">
                  ${{ formatPrice(trade.entry_price) }}
                </td>
                <td class="text-xs">
                  {{ formatDate(trade.exit_date) }}
                </td>
                <td class="font-mono">
                  ${{ formatPrice(trade.exit_price) }}
                </td>
                <td>
                  <span class="badge badge-sm badge-ghost">
                    {{ formatExitReason(trade.exit_reason) }}
                  </span>
                </td>
                <td
                  class="font-mono font-bold"
                  :class="trade.pnl_pct >= 0 ? 'text-success' : 'text-error'"
                >
                  {{ trade.pnl_pct >= 0 ? '+' : '' }}{{ trade.pnl_pct?.toFixed(3) }}%
                </td>
                <td>{{ trade.bars_held }} bars</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!hasData && !isLoading"
      class="bg-base-200 rounded-lg p-8 text-center"
    >
      <p class="text-lg text-base-content/50 mb-4">
        Select a symbol and click "Load Data" to view MACD Area analysis
      </p>
      <div class="text-sm text-base-content/40 max-w-2xl mx-auto">
        <p class="mb-2">
          <strong>MACD Area Ratio Strategy:</strong>
        </p>
        <ul class="text-left list-disc list-inside space-y-1">
          <li><strong>Wave Area:</strong> Cumulative sum of histogram values between zero-crossings</li>
          <li><strong>Area Ratio:</strong> Current wave area / Previous same-direction wave area</li>
          <li><strong>Signal:</strong> When ratio &lt; 0.65, momentum is exhausted</li>
          <li><strong>Entry:</strong> SHORT when bullish wave exhausted, LONG when bearish wave exhausted</li>
        </ul>
      </div>
    </div>

    <!-- Strategy Guide -->
    <div
      v-if="hasData"
      class="mt-6 collapse collapse-arrow bg-base-200 rounded-lg"
    >
      <input type="checkbox">
      <div class="collapse-title font-semibold">
        MACD Area Ratio Strategy - Trading Rules
      </div>
      <div class="collapse-content text-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div class="p-3 bg-success/10 rounded-lg">
            <h5 class="font-bold text-success mb-2">
              GO LONG When:
            </h5>
            <ul class="list-disc list-inside space-y-1 text-base-content/80">
              <li>A bearish MACD histogram wave just completed</li>
              <li>That wave's area is less than 65% of the previous bearish wave</li>
              <li><strong>Interpretation:</strong> Sellers are running out of steam, expect a bounce</li>
            </ul>
          </div>
          <div class="p-3 bg-error/10 rounded-lg">
            <h5 class="font-bold text-error mb-2">
              GO SHORT When:
            </h5>
            <ul class="list-disc list-inside space-y-1 text-base-content/80">
              <li>A bullish MACD histogram wave just completed</li>
              <li>That wave's area is less than 65% of the previous bullish wave</li>
              <li><strong>Interpretation:</strong> Buyers are running out of steam, expect a pullback</li>
            </ul>
          </div>
        </div>
        <div class="mt-4 p-3 bg-base-300 rounded-lg">
          <h5 class="font-bold mb-2">
            Exit Rules
          </h5>
          <table class="table table-xs">
            <thead>
              <tr>
                <th>Condition</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>+{{ profitTarget }}% profit</td>
                <td>Take profit</td>
              </tr>
              <tr>
                <td>-{{ stopLoss }}% loss</td>
                <td>Stop loss</td>
              </tr>
              <tr>
                <td>60 bars (1 hour on 1m)</td>
                <td>Time exit</td>
              </tr>
              <tr>
                <td>Opposite signal fires</td>
                <td>Reverse position</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import Plotly from 'plotly.js-dist-min';
import api from '@/api/axios';

const route = useRoute();

// Common symbols
const commonSymbols = [
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'XRPUSDT',
  'BNBUSDT',
  'ADAUSDT',
  'DOGEUSDT',
  'AVAXUSDT',
  'DOTUSDT',
  'MATICUSDT',
  'LINKUSDT',
  'UNIUSDT',
];

const timeframeOptions = [
  { value: '1m', label: '1m' },
  { value: '5m', label: '5m' },
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '4h', label: '4h' },
  { value: '1d', label: '1d' },
];

const dayOptionsByTimeframe = {
  '1m': [1, 3, 7],
  '5m': [3, 7, 14],
  '15m': [7, 14, 30],
  '1h': [14, 30, 60],
  '4h': [30, 60, 90],
  '1d': [30, 60, 90],
};

const thresholdOptions = [0.5, 0.65, 0.75, 0.85, 0.95, 1.0];

// State
const priceChart = ref(null);
const macdChart = ref(null);
const areaRatioChart = ref(null);
const equityChart = ref(null);

const symbolInput = ref('BTCUSDT');
const symbol = ref('BTCUSDT');
const showDropdown = ref(false);
const filteredSymbols = ref([...commonSymbols]);
const timeframe = ref('15m');
const days = ref(7);
const areaThreshold = ref(0.65);
const profitTarget = ref(0.3);
const stopLoss = ref(0.2);
const minWaveBars = ref(1);
const endDateTime = ref(formatDateTimeLocal(new Date()));
const activeTab = ref('analysis');

// Helper to format Date to datetime-local input format (YYYY-MM-DDTHH:MM)
function formatDateTimeLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Convert timeframe string to milliseconds
function timeframeToMs(tf) {
  const match = tf.match(/^(\d+)([mhd])$/);
  if (!match) return 60 * 60 * 1000; // Default 1h
  const value = parseInt(match[1]);
  const unit = match[2];
  switch (unit) {
    case 'm': return value * 60 * 1000;
    case 'h': return value * 60 * 60 * 1000;
    case 'd': return value * 24 * 60 * 60 * 1000;
    default: return 60 * 60 * 1000;
  }
}

const macdData = ref([]);
const wavesData = ref([]);
const backtest = ref({});
const currentWave = ref(null);
const formingData = ref(null);
const dataSource = ref(null);
const isLoading = ref(false);
const error = ref(null);

// Auto-refresh state
const autoRefreshEnabled = ref(false);
const autoRefreshCountdown = ref(0);
const autoRefreshInterval = ref(null);
const AUTO_REFRESH_SECONDS = 60;

// Computed
const hasData = computed(() => macdData.value.length > 0);

const availableDays = computed(() => {
  return dayOptionsByTimeframe[timeframe.value] || [7, 14, 30];
});

const recentWaves = computed(() => {
  return wavesData.value.slice(-20).reverse();
});

// Area ratio statistics
const areaRatioStats = computed(() => {
  const ratios = wavesData.value
    .filter(w => w.area_ratio !== null && w.area_ratio !== undefined)
    .map(w => w.area_ratio);

  if (ratios.length === 0) {
    return { min: null, max: null, avg: null, median: null, count: 0, belowThreshold: 0 };
  }

  const sorted = [...ratios].sort((a, b) => a - b);
  const sum = ratios.reduce((a, b) => a + b, 0);
  const belowThreshold = ratios.filter(r => r < areaThreshold.value).length;

  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    avg: sum / ratios.length,
    median: sorted[Math.floor(sorted.length / 2)],
    count: ratios.length,
    belowThreshold,
    signalPct: ((belowThreshold / ratios.length) * 100).toFixed(1),
  };
});

const signalCardClass = computed(() => {
  if (!currentWave.value?.signal) return 'bg-base-300 border border-base-content/20';
  if (currentWave.value.signal === 'LONG') return 'bg-success/10 border border-success/50';
  if (currentWave.value.signal === 'SHORT') return 'bg-error/10 border border-error/50';
  return 'bg-base-300 border border-base-content/20';
});

const signalBarClass = computed(() => {
  if (!currentWave.value?.signal) return 'bg-warning';
  if (currentWave.value.signal === 'LONG') return 'bg-success';
  if (currentWave.value.signal === 'SHORT') return 'bg-error';
  return 'bg-warning';
});

const confidenceBadgeClass = computed(() => {
  const confidence = currentWave.value?.signal_confidence;
  if (confidence === 'HIGH') return 'badge-success';
  if (confidence === 'MEDIUM') return 'badge-warning';
  return 'badge-ghost';
});

const areaRatioColor = computed(() => {
  const ratio = currentWave.value?.area_ratio;
  if (ratio === null || ratio === undefined) return '';
  if (ratio < areaThreshold.value) return 'text-error';
  if (ratio < 0.8) return 'text-warning';
  return 'text-success';
});

// Forming signal card styles
const formingSignalCardClass = computed(() => {
  if (!formingData.value?.projected_signal) return 'bg-base-300/50 border-base-content/30';
  if (formingData.value.projected_signal === 'LONG') return 'bg-success/5 border-success/40';
  if (formingData.value.projected_signal === 'SHORT') return 'bg-error/5 border-error/40';
  return 'bg-base-300/50 border-base-content/30';
});

const formingSignalBarClass = computed(() => {
  if (!formingData.value?.projected_signal) return 'bg-info/50';
  if (formingData.value.projected_signal === 'LONG') return 'bg-success/70';
  if (formingData.value.projected_signal === 'SHORT') return 'bg-error/70';
  return 'bg-info/50';
});

const formingConfidenceBadgeClass = computed(() => {
  const confidence = formingData.value?.projected_confidence;
  if (confidence === 'HIGH') return 'badge-success';
  if (confidence === 'MEDIUM') return 'badge-warning';
  return 'badge-ghost';
});

const formingRatioColor = computed(() => {
  const ratio = formingData.value?.projected_ratio;
  if (ratio === null || ratio === undefined) return '';
  if (ratio < areaThreshold.value) return 'text-error';
  if (ratio < 0.8) return 'text-warning';
  return 'text-success';
});

const formattedDataSource = computed(() => {
  if (!dataSource.value) return null;
  const exchangeNames = {
    'bybit': 'Bybit',
    'okx': 'OKX',
    'kucoin': 'KuCoin',
    'gate': 'Gate.io',
    'cached': 'Cached',
    'default': 'Default',
  };
  return {
    exchange: exchangeNames[dataSource.value.exchange] || dataSource.value.exchange,
    lastCandle: dataSource.value.last_candle,
  };
});

// Helpers
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.replace('T', ' ').substring(0, 16);
};

const formatPrice = (price) => {
  if (typeof price !== 'number') return '0.00';
  if (price >= 1000) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(price);
};

const formatExitReason = (reason) => {
  const mapping = {
    'PROFIT_TARGET': 'Profit Target',
    'STOP_LOSS': 'Stop Loss',
    'TIME_EXIT': 'Time Exit',
    'SIGNAL_REVERSAL': 'Signal Reversal',
  };
  return mapping[reason] || reason;
};

const adjustEndDateTime = (direction) => {
  stopAutoRefresh(); // Stop auto-refresh when navigating historically
  const current = new Date(endDateTime.value);
  const deltaMs = timeframeToMs(timeframe.value) * direction;
  const newDate = new Date(current.getTime() + deltaMs);
  const now = new Date();

  // Don't allow going into the future
  if (newDate <= now) {
    endDateTime.value = formatDateTimeLocal(newDate);
    if (hasData.value) {
      fetchMACDData();
    }
  }
};

const resetEndDateTime = () => {
  endDateTime.value = formatDateTimeLocal(new Date());
  if (hasData.value) {
    fetchMACDData();
  }
};

// Auto-refresh for forming candle updates
const startAutoRefresh = () => {
  stopAutoRefresh();
  autoRefreshEnabled.value = true;
  autoRefreshCountdown.value = AUTO_REFRESH_SECONDS;

  autoRefreshInterval.value = setInterval(() => {
    autoRefreshCountdown.value--;
    if (autoRefreshCountdown.value <= 0) {
      autoRefreshCountdown.value = AUTO_REFRESH_SECONDS;
      // Reset end time to now so we get fresh forming candle
      endDateTime.value = formatDateTimeLocal(new Date());
      fetchMACDData();
    }
  }, 1000);
};

const stopAutoRefresh = () => {
  autoRefreshEnabled.value = false;
  if (autoRefreshInterval.value) {
    clearInterval(autoRefreshInterval.value);
    autoRefreshInterval.value = null;
  }
};

const filterSymbols = () => {
  const query = symbolInput.value.toUpperCase();
  filteredSymbols.value = commonSymbols.filter(s => s.includes(query));
  showDropdown.value = true;
};

const selectSymbol = (sym) => {
  symbolInput.value = sym;
  symbol.value = sym;
  showDropdown.value = false;
};

// Fetch data
const fetchMACDData = async () => {
  if (symbolInput.value && symbolInput.value !== symbol.value) {
    symbol.value = symbolInput.value.toUpperCase().replace('/', '').trim();
  }

  if (!symbol.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get('/api/market/macd-area/', {
      params: {
        symbol: symbol.value,
        days: days.value,
        timeframe: timeframe.value,
        threshold: areaThreshold.value,
        end_date: endDateTime.value,
        profit_target: profitTarget.value,
        stop_loss: stopLoss.value,
        min_wave_bars: minWaveBars.value,
      },
    });

    if (response.data) {
      macdData.value = response.data.data || [];
      wavesData.value = response.data.waves || [];
      backtest.value = response.data.backtest || {};
      currentWave.value = response.data.current || null;
      formingData.value = response.data.forming || null;
      dataSource.value = response.data.data_source || null;

      await nextTick();
      renderAllCharts();
    } else {
      error.value = 'No data returned from API';
    }
  } catch (err) {
    console.error('Error fetching MACD data:', err);
    error.value = err.response?.data?.error || err.message || 'Failed to fetch data';
  } finally {
    isLoading.value = false;
  }
};

// Chart config
const commonLayout = {
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  font: { color: '#9ca3af', size: 11 },
  margin: { t: 10, b: 40, l: 60, r: 20 },
  showlegend: false,
  xaxis: {
    gridcolor: 'rgba(156, 163, 175, 0.1)',
    linecolor: 'rgba(156, 163, 175, 0.2)',
  },
  yaxis: {
    gridcolor: 'rgba(156, 163, 175, 0.1)',
    linecolor: 'rgba(156, 163, 175, 0.2)',
  },
};

const commonConfig = {
  responsive: true,
  displayModeBar: false,
};

// Render charts
const renderAllCharts = () => {
  if (!hasData.value) return;
  renderPriceChart();
  renderMACDChart();
  renderAreaRatioChart();
  if (activeTab.value === 'backtest' && backtest.value.equity_curve) {
    renderEquityChart();
  }
};

const renderPriceChart = () => {
  if (!priceChart.value) return;

  const data = macdData.value;
  const dates = data.map(d => d.date);
  const opens = data.map(d => d.open);
  const highs = data.map(d => d.high);
  const lows = data.map(d => d.low);
  const closes = data.map(d => d.close);

  // Build wave background shapes
  const shapes = [];
  const annotations = [];

  for (const wave of wavesData.value) {
    const startIdx = data.findIndex(d => d.date === wave.start_date);
    const endIdx = data.findIndex(d => d.date === wave.end_date);

    if (startIdx >= 0 && endIdx >= 0) {
      const waveData = data.slice(startIdx, endIdx + 1);
      const minPrice = Math.min(...waveData.map(d => d.low)) * 0.999;
      const maxPrice = Math.max(...waveData.map(d => d.high)) * 1.001;

      shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: wave.start_date,
        x1: wave.end_date,
        y0: minPrice,
        y1: maxPrice,
        fillcolor: wave.direction === 'bullish' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
        line: { width: 0 },
        layer: 'below',
      });

      // Add signal markers
      if (wave.signal) {
        if (wave.signal_date) {
          // Confirmed: filled arrow at the crossing bar
          annotations.push({
            x: wave.signal_date,
            y: wave.signal === 'SHORT' ? maxPrice : minPrice,
            text: wave.signal === 'SHORT' ? '▼' : '▲',
            showarrow: false,
            font: {
              size: 16,
              color: wave.signal === 'SHORT' ? '#ef4444' : '#22c55e',
            },
          });
        } else {
          // Potential: unfilled arrow at wave end (crossing bar not yet in data)
          annotations.push({
            x: wave.end_date,
            y: wave.signal === 'SHORT' ? maxPrice : minPrice,
            text: wave.signal === 'SHORT' ? '▽' : '△',
            showarrow: false,
            font: {
              size: 16,
              color: wave.signal === 'SHORT' ? '#ef4444' : '#22c55e',
            },
          });
        }
      }
    }
  }

  Plotly.react(priceChart.value, [{
    x: dates,
    open: opens,
    high: highs,
    low: lows,
    close: closes,
    type: 'candlestick',
    increasing: { line: { color: '#26a69a' }, fillcolor: '#26a69a' },
    decreasing: { line: { color: '#ef5350' }, fillcolor: '#ef5350' },
  }], {
    ...commonLayout,
    xaxis: { ...commonLayout.xaxis, rangeslider: { visible: false } },
    yaxis: { ...commonLayout.yaxis, title: 'Price' },
    shapes,
    annotations,
    margin: { t: 20, b: 40, l: 70, r: 20 },
  }, commonConfig);
};

const renderMACDChart = () => {
  if (!macdChart.value) return;

  const data = macdData.value;
  const dates = data.map(d => d.date);
  const histogram = data.map(d => d.histogram);
  const macdLine = data.map(d => d.macd);
  const signalLine = data.map(d => d.signal);

  const colors = histogram.map(h => {
    if (h === null) return 'rgba(128, 128, 128, 0.5)';
    return h >= 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)';
  });

  Plotly.react(macdChart.value, [
    {
      x: dates,
      y: histogram,
      type: 'bar',
      marker: { color: colors },
      name: 'Histogram',
    },
    {
      x: dates,
      y: macdLine,
      type: 'scatter',
      mode: 'lines',
      line: { color: '#3b82f6', width: 1.5 },
      name: 'MACD',
    },
    {
      x: dates,
      y: signalLine,
      type: 'scatter',
      mode: 'lines',
      line: { color: '#f59e0b', width: 1.5 },
      name: 'Signal',
    },
  ], {
    ...commonLayout,
    yaxis: { ...commonLayout.yaxis, title: 'MACD' },
    showlegend: true,
    legend: { x: 0, y: 1.1, orientation: 'h' },
  }, commonConfig);
};

const renderAreaRatioChart = () => {
  if (!areaRatioChart.value) return;

  // Get area ratios from waves at their end dates
  const waveRatios = wavesData.value
    .filter(w => w.area_ratio !== null)
    .map(w => ({
      date: w.end_date,
      ratio: w.area_ratio,
      direction: w.direction,
      signal: w.signal,
    }));

  const dates = waveRatios.map(w => w.date);
  const ratios = waveRatios.map(w => w.ratio);
  const colors = waveRatios.map(w => {
    if (w.signal) return w.signal === 'LONG' ? '#22c55e' : '#ef4444';
    return w.direction === 'bullish' ? '#3b82f6' : '#f59e0b';
  });

  Plotly.react(areaRatioChart.value, [
    {
      x: dates,
      y: ratios,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: '#8b5cf6', width: 2 },
      marker: { color: colors, size: 8 },
    },
  ], {
    ...commonLayout,
    yaxis: { ...commonLayout.yaxis, title: 'Area Ratio' },
    shapes: [
      {
        type: 'line',
        x0: dates[0],
        x1: dates[dates.length - 1],
        y0: areaThreshold.value,
        y1: areaThreshold.value,
        line: { color: '#ef4444', width: 2, dash: 'dash' },
      },
      {
        type: 'line',
        x0: dates[0],
        x1: dates[dates.length - 1],
        y0: 1,
        y1: 1,
        line: { color: '#6b7280', width: 1 },
      },
    ],
    annotations: [
      {
        x: dates[dates.length - 1],
        y: areaThreshold.value,
        text: `Threshold (${areaThreshold.value})`,
        showarrow: false,
        xanchor: 'right',
        font: { size: 10, color: '#ef4444' },
      },
    ],
  }, commonConfig);
};

const renderEquityChart = () => {
  if (!equityChart.value || !backtest.value.equity_curve) return;

  const curve = backtest.value.equity_curve;
  const dates = curve.map(c => c.date);
  const equity = curve.map(c => c.equity);

  Plotly.react(equityChart.value, [{
    x: dates,
    y: equity,
    type: 'scatter',
    mode: 'lines',
    fill: 'tozeroy',
    line: { color: '#3b82f6', width: 2 },
    fillcolor: 'rgba(59, 130, 246, 0.1)',
  }], {
    ...commonLayout,
    yaxis: { ...commonLayout.yaxis, title: 'Equity (%)' },
    shapes: [
      {
        type: 'line',
        x0: dates[0],
        x1: dates[dates.length - 1],
        y0: 100,
        y1: 100,
        line: { color: '#6b7280', width: 1, dash: 'dash' },
      },
    ],
  }, commonConfig);
};

// Event handlers
const handleClickOutside = (event) => {
  if (!event.target.closest('.dropdown') && !event.target.closest('.relative')) {
    showDropdown.value = false;
  }
};

const handleResize = () => {
  if (hasData.value) {
    [priceChart, macdChart, areaRatioChart, equityChart].forEach(chart => {
      if (chart.value) {
        Plotly.Plots.resize(chart.value);
      }
    });
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);

  // Auto-load symbol from query param (e.g., ?symbol=FARTCOINUSDT)
  const querySymbol = route.query.symbol;
  if (querySymbol) {
    const sym = String(querySymbol).toUpperCase().replace('/', '').trim();
    symbolInput.value = sym;
    symbol.value = sym;
    nextTick(() => fetchMACDData());
  }
});

onBeforeUnmount(() => {
  stopAutoRefresh();
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  [priceChart, macdChart, areaRatioChart, equityChart].forEach(chart => {
    if (chart.value) {
      Plotly.purge(chart.value);
    }
  });
});

// Watchers
watch(days, () => {
  if (hasData.value) {
    fetchMACDData();
  }
});

watch(timeframe, (newTf) => {
  const validDays = dayOptionsByTimeframe[newTf] || [7, 14, 30];
  if (!validDays.includes(days.value)) {
    days.value = validDays[0];
  }
  if (hasData.value) {
    fetchMACDData();
  }
});

watch(areaThreshold, () => {
  if (hasData.value) {
    fetchMACDData();
  }
});

watch(activeTab, () => {
  nextTick(() => {
    if (activeTab.value === 'analysis') {
      // Re-render analysis charts when switching to analysis tab
      renderPriceChart();
      renderMACDChart();
      renderAreaRatioChart();
    } else if (activeTab.value === 'backtest' && backtest.value.equity_curve) {
      // Render equity chart when switching to backtest tab
      renderEquityChart();
    }
  });
});
</script>

<style scoped>
.macd-explorer-tab {
  width: 100%;
}

.chart-panel-large {
  width: 100%;
  height: 350px;
}

.chart-panel {
  width: 100%;
  height: 200px;
}

@media (max-width: 639px) {
  .chart-container {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel-large {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel,
  .analytics-chart {
    height: 280px;
    min-height: 240px;
  }
}
</style>
